import type { TFunction } from "i18next";

import type {
  PartnerBenefitDiscountStage,
  PartnerBenefitStatus,
} from "@/services/authService";

/**
 * Turns the raw `PartnerBenefitStatus` (partner name + stage timeline) into
 * copy for the plan-picker banner: what's discounted right now, for how
 * much longer, and what happens after — so a tenant sees WHY their price is
 * lower, not just the lower number.
 */
export interface PartnerBenefitCopy {
  /** "50%" or "$500" — the current stage's discount, for the headline. */
  amountLabel: string;
  /** Full sentence: "50% de descuento durante los próximos 3 meses." */
  durationSentence: string;
  /** "Después, 25% de descuento durante 3 meses más." — null on the last stage. */
  nextStageSentence: string | null;
}

function stageAmountLabel(
  t: TFunction,
  stage: PartnerBenefitDiscountStage,
): string {
  return stage.discountType === "percent"
    ? t("partnerBenefit.amountPercent", "{{value}}%", {
        value: stage.discountValue,
      })
    : t("partnerBenefit.amountFlat", "${{value}}", {
        value: (stage.discountValue / 100).toLocaleString("es-UY"),
      });
}

export function computePartnerBenefitCopy(
  t: TFunction,
  status: PartnerBenefitStatus,
): PartnerBenefitCopy {
  const stage = status.stages[status.currentStageIndex];
  let cursor = 0;
  for (let i = 0; i < status.currentStageIndex; i++) {
    cursor += status.stages[i].durationMonths;
  }
  // Whole calendar months elapsed since redemption, so a tenant who redeemed
  // 0 months ago is 1 month INTO the first stage, not 0 — durationMonths=3
  // then correctly reads "3 meses restantes", not "2".
  const monthsIntoStage = status.monthsElapsed - cursor + 1;
  const monthsRemaining = Math.max(1, stage.durationMonths - monthsIntoStage + 1);

  const amountLabel = stageAmountLabel(t, stage);
  const durationSentence = t("partnerBenefit.duration", {
    count: monthsRemaining,
    amount: amountLabel,
  });

  const nextStage = status.stages[status.currentStageIndex + 1] ?? null;
  const nextStageSentence = nextStage
    ? t("partnerBenefit.nextStage", {
        count: nextStage.durationMonths,
        amount: stageAmountLabel(t, nextStage),
      })
    : null;

  return { amountLabel, durationSentence, nextStageSentence };
}
